<script setup lang="ts">
// `chart` / `table` / `matrix` / `card` / `double` → module 08's `DataVisible` (§10).
//
// This component is deliberately thin, and that is the acceptance criterion rather than an
// accident: 09 adds NO report chrome. Loading, "no data for range", the error retry and the ready
// render all belong to `DataVisible` (§14), so the only thing here is the widget→props mapping,
// which lives in the pure `widgetToReportProps` so it can be proven without mounting anything.
//
import { computed } from 'vue'

import { useWidgetFieldUniverse } from '../../composables/useWidgetFieldUniverse'
import type { DashboardWidget } from '../../models/widget.types'
import {
  widgetToReportProps,
  widgetWorkspaceIds,
  type WidgetRenderContext,
} from '../../service/widgetSpec'

import WidgetUnavailable from './WidgetUnavailable.vue'

import { DataVisible } from '@/features/reports'
import { useI18n } from '@/shared/i18n'

const props = withDefaults(
  defineProps<{
    widget: DashboardWidget
    title?: string
    wsId?: number | string | null
    accountWorkspaces?: readonly (number | string)[]
    targetCurrency?: string | null
    defaultPostData?: string
    readOnly?: boolean
  }>(),
  {
    title: '',
    wsId: null,
    accountWorkspaces: () => [],
    targetCurrency: null,
    defaultPostData: '',
    readOnly: false,
  },
)
const { t } = useI18n()

const context = computed<WidgetRenderContext>(() => ({
  defaultPostData: props.defaultPostData,
  wsId: props.wsId,
  accountWorkspaces: props.accountWorkspaces,
  targetCurrency: props.targetCurrency,
}))

const availableFields = useWidgetFieldUniverse(
  () => props.widget,
  () => widgetWorkspaceIds(props.widget.wire, context.value),
  () => props.accountWorkspaces.length > 0,
)

/**
 * Bound as one object so the optional props stay ABSENT rather than explicitly `undefined` —
 * `exactOptionalPropertyTypes` (contract 01 §3) rejects the latter. `workspaceIds` and
 * `targetCurrency` ride the same object; `DataVisible` threads both since ORP-19956.
 */
const composite = computed(() => {
  const mapped = widgetToReportProps(props.widget, context.value)
  if (!mapped) return null
  return {
    ...mapped,
    ...(availableFields.value.length ? { availableFields: availableFields.value } : {}),
    readOnly: props.readOnly,
    ariaLabel: props.title || t('dashboards.widget.untitled'),
    emptyLabel: t('reports.section.empty'),
    errorLabel: t('reports.section.error'),
    retryLabel: t('reports.section.retry'),
  }
})
</script>

<template>
  <DataVisible v-if="composite" v-bind="composite" />
  <WidgetUnavailable v-else />
</template>
